/**
 * Web Audio engine (Oe) — beds, muffling, beat clock.
 */
import { ge, _e } from "./paths.js";
import { xe, j, Ce, we } from "./beats.js";

export function Ee(e, t) {
    e.positionX.value = t.x, e.positionY.value = t.y, e.positionZ.value = -t.z
}
export const De = {
        master: .9,
        music: .85,
        sfx: .8,
        ambience: .7,
        ui: .76
    };
export class Oe {
        ctx = null;
        buses = {};
        reverbs = {};
        noise = null;
        brown = null;
        started = !1;
        master;
        limiter;
        musicBuffer = null;
        forestBuffer = null;
        musicInput;
        musicFilter;
        musicBass;
        musicPanner;
        musicSpatial;
        musicWide;
        musicLevel;
        musicPipeSend;
        musicHallSend;
        analyser;
        fft = new Uint8Array;
        forestGain;
        pipeGain;
        clubGain;
        songZero = 0;
        clockOffset = NaN;
        decoderShift = 0;
        muffleNow = 1;
        async preload(e) {
            let t = window.AudioContext ?? window.webkitAudioContext;
            if (!t) return;
            let n = new t({
                latencyHint: `interactive`
            });
            this.ctx = n, this.buildGraph(n);
            let r = [0, 0],
                i = () => e(r[0] * .8 + r[1] * .2),
                [a, o] = await Promise.all([ge, _e].map((e, t) => je(e, e => (r[t] = e, i())).then(e => n.decodeAudioData(e.buffer))));
            this.musicBuffer = a, this.forestBuffer = o, this.decoderShift = Me(a)
        }
        buildGraph(e) {
            this.master = e.createGain(), this.master.gain.value = De.master ** 2, this.limiter = e.createDynamicsCompressor(), this.limiter.threshold.value = -8, this.limiter.knee.value = 6, this.limiter.ratio.value = 12, this.limiter.attack.value = .003, this.limiter.release.value = .18, this.master.connect(this.limiter).connect(e.destination);
            for (let t of [`music`, `sfx`, `ambience`, `ui`]) this.buses[t] = M(e, De[t] ** 2), this.buses[t].connect(this.master);
            this.noise = Ne(e, 2, `white`), this.brown = Ne(e, 4, `brown`);
            let t = M(e, .9);
            t.connect(this.buses.sfx);
            for (let [n, r] of [
                    [`pipe`, Pe(e, 3.4, 2.2, 5200, [.021, .043, .067, .09])],
                    [`hall`, Pe(e, 2.2, 3.4, 3800, [.013, .029])]
                ]) {
                let i = e.createConvolver();
                i.buffer = r, this.reverbs[n] = M(e, 1), this.reverbs[n].connect(i).connect(t)
            }
            this.musicInput = M(e, 0), this.analyser = e.createAnalyser(), this.analyser.fftSize = 1024, this.analyser.smoothingTimeConstant = .55, this.fft = new Uint8Array(this.analyser.frequencyBinCount), this.musicInput.connect(this.analyser), this.musicFilter = ke(e, `lowpass`, 200, .7), this.musicBass = ke(e, `lowshelf`, 120), this.musicInput.connect(this.musicFilter).connect(this.musicBass), this.musicPanner = e.createPanner(), this.musicPanner.panningModel = `HRTF`, this.musicPanner.distanceModel = `inverse`, this.musicPanner.refDistance = 1e4, this.musicPanner.rolloffFactor = 0, this.musicSpatial = M(e, 1), this.musicWide = M(e, 0), this.musicLevel = M(e, .4), this.musicBass.connect(this.musicPanner).connect(this.musicSpatial).connect(this.musicLevel), this.musicBass.connect(this.musicWide).connect(this.musicLevel), this.musicLevel.connect(this.buses.music), this.musicPipeSend = M(e, 0), this.musicHallSend = M(e, 0), this.musicLevel.connect(this.musicPipeSend).connect(this.reverbs.pipe), this.musicLevel.connect(this.musicHallSend).connect(this.reverbs.hall), this.forestGain = M(e, 1), this.forestGain.connect(this.buses.ambience), this.pipeGain = M(e, 0), this.pipeGain.connect(this.buses.ambience), this.pipeGain.connect(this.reverbs.pipe), this.clubGain = M(e, 0), this.clubGain.connect(this.buses.ambience)
        }
        setMusicPosition(e) {
            this.ctx && Ee(this.musicPanner, e)
        }
        async unlock() {
            let e = this.ctx;
            if (!e || this.started) return;
            let t = [`pointerdown`, `pointerup`, `touchend`, `keydown`, `click`],
                n = async () => {
                    try {
                        await e.resume()
                    } catch {}
                    if (e.state === `running`) {
                        for (let e of t) window.removeEventListener(e, n, !0);
                        this.beginPlayback()
                    }
                };
            for (let e of t) window.addEventListener(e, n, !0);
            try {
                await e.resume()
            } catch {}
            e.state === `running` && this.beginPlayback()
        }
        beginPlayback() {
            let e = this.ctx;
            if (!e || this.started || e.state !== `running`) return;
            this.started = !0, this.startBeds(), this.startMusic(e.currentTime + .05, 2.5)
        }
        startBeds() {
            let e = this.ctx;
            this.forestBuffer && Ae(e, this.forestBuffer).connect(this.forestGain), Ae(e, this.brown).connect(ke(e, `lowpass`, 260)).connect(M(e, .55)).connect(this.pipeGain);
            let t = ke(e, `bandpass`, 1400, 1.4),
                n = e.createOscillator();
            n.frequency.value = .7, n.connect(M(e, 500)).connect(t.frequency), n.start(), Ae(e, this.noise).connect(t).connect(M(e, .06)).connect(this.pipeGain), Ae(e, this.brown).connect(ke(e, `bandpass`, 520, .6)).connect(M(e, .5)).connect(this.clubGain)
        }
        startMusic(e, t) {
            if (!this.ctx || !this.musicBuffer) return;
            let n = this.ctx.createBufferSource();
            n.buffer = this.musicBuffer, n.loop = !0, n.connect(this.musicInput), n.start(e), this.songZero = e, this.musicInput.gain.setValueAtTime(0, e), this.musicInput.gain.linearRampToValueAtTime(1, e + t)
        }
        tickClock() {
            let e = this.ctx;
            if (!e) return;
            let t = e.getOutputTimestamp?.(),
                n;
            n = t?.contextTime !== void 0 && t.performanceTime !== void 0 && t.performanceTime > 0 ? t.contextTime - t.performanceTime / 1e3 : e.currentTime - (e.outputLatency || e.baseLatency || 0) - performance.now() / 1e3, !Number.isFinite(this.clockOffset) || Math.abs(n - this.clockOffset) > .06 ? this.clockOffset = n : this.clockOffset += (n - this.clockOffset) * .04
        }
        beat() {
            if (!this.started || !this.musicBuffer) return performance.now() / 1e3 / Ce;
            Number.isFinite(this.clockOffset) || this.tickClock();
            let e = performance.now() / 1e3 + this.clockOffset - this.songZero,
                t = this.musicBuffer.duration,
                n = e > 0 ? Math.floor(e / t) : 0;
            return we((e > 0 ? e % t : e) - this.decoderShift) + n * j.length
        }
        get now() {
            return this.ctx?.currentTime ?? performance.now() / 1e3
        }
        setMuted(e) {
            this.master?.gain.setTargetAtTime(e ? 0 : De.master ** 2, this.now, .05)
        }
        update(e, t, n, r) {
            let i = this.ctx;
            if (!i) return;
            let a = i.currentTime,
                o = i.listener;
            o.positionX.setTargetAtTime(e.x, a, .02), o.positionY.setTargetAtTime(e.y, a, .02), o.positionZ.setTargetAtTime(-e.z, a, .02), o.forwardX.setTargetAtTime(t.x, a, .02), o.forwardY.setTargetAtTime(t.y, a, .02), o.forwardZ.setTargetAtTime(-t.z, a, .02), this.muffleNow += (n.muffle - this.muffleNow) * Math.min(1, r * 3);
            let s = this.muffleNow;
            this.musicFilter.frequency.setTargetAtTime(19e3 * (170 / 19e3) ** s, a, .05), this.musicBass.gain.setTargetAtTime(7 * s, a, .2), this.musicLevel.gain.setTargetAtTime(n.level, a, .12), this.musicSpatial.gain.setTargetAtTime(1 - n.wide * .85, a, .2), this.musicWide.gain.setTargetAtTime(n.wide, a, .2), this.musicPipeSend.gain.setTargetAtTime(n.pipeVerb * .55, a, .2), this.musicHallSend.gain.setTargetAtTime(n.hallVerb * .12, a, .2), this.forestGain.gain.setTargetAtTime(n.forest * .9, a, .3), this.pipeGain.gain.setTargetAtTime(n.pipe * .8, a, .3), this.clubGain.gain.setTargetAtTime(n.club * .35, a, .3)
        }
        bass() {
            if (!this.ctx) return 0;
            this.analyser.getByteFrequencyData(this.fft);
            let e = this.ctx.sampleRate / this.analyser.fftSize,
                t = Math.max(1, Math.floor(30 / e)),
                n = Math.min(this.fft.length - 1, Math.ceil(140 / e)),
                r = 0;
            for (let e = t; e <= n; e++) r += this.fft[e];
            return r / ((n - t + 1) * 255)
        }
}

export function M(e, t) {
    let n = e.createGain();
    return n.gain.value = t, n
}

export function ke(e, t, n, r) {
    let i = e.createBiquadFilter();
    return i.type = t, i.frequency.value = n, r !== void 0 && (i.Q.value = r), i
}

export function Ae(e, t) {
    let n = e.createBufferSource();
    return n.buffer = t, n.loop = !0, n.start(), n
}
export async function je(e, t) {
    let n = await fetch(e),
        r = Number(n.headers.get(`content-length`)) || 0,
        i = n.body?.getReader();
    if (!i) return new Uint8Array(await n.arrayBuffer());
    let a = [],
        o = 0;
    for (;;) {
        let {
            done: e,
            value: n
        } = await i.read();
        if (e) break;
        a.push(n), o += n.length, r && t(o / r)
    }
    let s = new Uint8Array(o),
        c = 0;
    for (let e of a) s.set(e, c), c += e.length;
    return t(1), s
}

export function Me(e) {
    let t = e.getChannelData(0),
        n = e.numberOfChannels > 1 ? e.getChannelData(1) : t,
        r = Math.min(t.length, Math.floor(e.sampleRate * .5));
    for (let i = 0; i < r; i++)
        if (Math.abs((t[i] + n[i]) * .5) > .02) {
            let t = i / e.sampleRate - xe;
            return Math.abs(t) < .1 ? t : 0
        } return 0
}

export function Ne(e, t, n) {
    let r = e.createBuffer(1, Math.floor(e.sampleRate * t), e.sampleRate),
        i = r.getChannelData(0),
        a = 0;
    for (let e = 0; e < i.length; e++) {
        let t = Math.random() * 2 - 1;
        n === `white` ? i[e] = t : i[e] = (a = (a + .02 * t) / 1.02) * 3.5
    }
    return r
}

export function Pe(e, t, n, r, i) {
    let a = e.sampleRate,
        o = Math.floor(a * t),
        s = e.createBuffer(2, o, a),
        c = Math.exp(-2 * Math.PI * r / a);
    for (let e = 0; e < 2; e++) {
        let r = s.getChannelData(e),
            l = 0;
        for (let e = 0; e < o; e++) {
            let i = e / a,
                o = (1 - i / t) ** n * Math.exp(-i * 1.5);
            l = l * c + (Math.random() * 2 - 1) * o * (1 - c), r[e] = l * 2.2
        }
        for (let t of i) {
            let n = Math.floor((t + (e ? .0031 : 0)) * a);
            n < o && (r[n] += .55 * Math.exp(-t * 12))
        }
    }
    return s
}

export { Oe as AudioEngine, De as BUS_LEVELS };
